/**
 * RacksList — listado de Racks (estándar de listados del CMDB).
 *
 * Igual que Ubicaciones: el andamiaje es `common/ListPage.jsx` (buscador,
 * filtros, exportar, columnas, clic en fila para abrir la ficha EN LECTURA,
 * botones [acciones propias] · Copiar · Editar · Eliminar, diálogos comunes).
 *
 * Lo propio de esta pantalla:
 *  · Vistas: **Lista** (por defecto) y **Fichas**.
 *  · Filtros: Ubicación, Estado, Ocupación (con hueco libre / llenos) y Altura (U).
 *  · Acciones propias: **Ver rack** (visor gráfico) y **Ver elementos montados**.
 *  · La columna de ocupación usa `occupied_u_front` / `free_units_front`, que
 *    calcula el backend en una sola consulta para toda la lista.
 *
 * Dos detalles del modelo que conviene tener presentes:
 *  · Borrar un rack es FÍSICO: se lleva por delante sus elementos montados (lo
 *    dice el propio diálogo de confirmación, con su recuento).
 *  · La ruta `/racks/:locationId` (a la que se llega desde Ubicaciones) entra
 *    con la ubicación ya filtrada.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RectangleStackIcon,
  Square3Stack3DIcon,
  ListBulletIcon,
  Squares2X2Icon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import RackFormElegant from './RackFormElegant';
import PromoteRackDialog from './PromoteRackDialog';
import RacksFrontView from './RacksFrontView';
import HelpDot from '../academy/HelpDot';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import api from '../services/api';
import { getServerFormStyle } from '../utils/serverFormStyle';

function RacksList({ companyId }) {
  const { t } = useTranslation();
  const { locationId } = useParams();
  const navigate = useNavigate();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const [racks, setRacks] = useState([]);
  const [estiloFicha] = useState(getServerFormStyle);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [selectedRack, setSelectedRack] = useState(null);

  const [rackToCopy, setRackToCopy] = useState(null);
  const [rackToInspect, setRackToInspect] = useState(null);
  const [rackToPromote, setRackToPromote] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const [search, setSearch] = useState('');
  const [filterDesign, setFilterDesign] = useState('all');
  const [filterLocation, setFilterLocation] = useState(locationId ? String(locationId) : '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOccupancy, setFilterOccupancy] = useState('');
  const [filterHeight, setFilterHeight] = useState('');

  const fetchRacks = async () => {
    try {
      setLoading(true);
      setError('');
      if (!companyId) {
        setError(tr('msg.select_company', 'Selecciona una empresa para ver racks.'));
        setRacks([]);
        return;
      }
      // `include_inactive`: el estado «Inactivo» es un borrado lógico. Sin
      // esto el rack desaparecía del listado y no había forma de reactivarlo
      // (el filtro de inactivos no tenía nada que enseñar).
      const response = await api.getRacks(undefined, companyId, false, true);
      setRacks(response.data || []);
    } catch (err) {
      setError(err.message);
      setRacks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // La ruta /racks/:locationId entra filtrada por esa ubicación.
  useEffect(() => {
    setFilterLocation(locationId ? String(locationId) : '');
  }, [locationId]);

  useEffect(() => {
    if (!locationId || !companyId) { setLocation(null); return undefined; }
    let cancelled = false;
    api.getLocation(locationId, companyId)
      .then((resp) => { if (!cancelled) setLocation(resp.data || resp); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [locationId, companyId]);

  /* ── Apertura de la ficha: lectura por defecto ──────────────────────── */
  const handleOpen = (rack) => {
    setSelectedRack(rack);
    setFormMode('view');
    setShowForm(true);
  };
  const handleEdit = (rack) => {
    setSelectedRack(rack);
    setFormMode('edit');
    setShowForm(true);
  };
  const handleCreate = () => {
    setSelectedRack(null);
    setFormMode('edit');
    setShowForm(true);
  };
  useAutoOpen(racks, handleOpen);
  const handleFormClose = () => {
    setShowForm(false);
    setSelectedRack(null);
    fetchRacks();
  };

  /* ── Borrado (físico: se lleva los elementos montados) ──────────────── */
  const handleDelete = async (rack) => {
    try {
      await api.deleteRack(rack.id, companyId);
      await fetchRacks();
    } catch (err) {
      setError(err.message || tr('rack.delete_error', 'No se pudo eliminar el rack'));
    }
  };

  /* ── Copiar ─────────────────────────────────────────────────────────── */
  const handleCopy = async (name) => {
    setBusy(true);
    setDialogError('');
    try {
      await api.cloneRack(rackToCopy.id, { name }, companyId);
      setRackToCopy(null);
      await fetchRacks();
    } catch (err) {
      setDialogError(err.message || tr('rack.copy_error', 'No se pudo copiar el rack'));
    } finally {
      setBusy(false);
    }
  };

  /* ── Pasar un diseño a producción ───────────────────────────────────── */
  const openPromote = (rack) => setRackToPromote(rack);

  /* ── Ver elementos montados ─────────────────────────────────────────── */
  const openInspect = async (rack) => {
    setRackToInspect(rack);
    setInspectData(null);
    setInspectLoading(true);
    setDialogError('');
    try {
      const resp = await api.getRack(rack.id, companyId);
      setInspectData(resp.data || resp);
    } catch (err) {
      setDialogError(err.message || tr('rack.elements_error', 'No se pudieron cargar los elementos'));
    } finally {
      setInspectLoading(false);
    }
  };

  /* ── Datos derivados ────────────────────────────────────────────────── */
  const heightOptions = useMemo(() => {
    const heights = [...new Set(racks.map((r) => r.height_u).filter(Boolean))]
      .sort((a, b) => a - b);
    return heights.map((h) => ({ value: String(h), label: `${h} U` }));
  }, [racks]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    racks.forEach((r) => {
      if (r.location_id != null && r.location_name) map.set(String(r.location_id), r.location_name);
    });
    return [...map.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es', { sensitivity: 'base' }))
      .map(([value, label]) => ({ value, label }));
  }, [racks]);

  const isFull = (rack) => (rack.free_units_front ?? null) === 0;

  const filteredRacks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return racks.filter((rack) => {
      if (term) {
        const haystack = [
          rack.name, rack.code, rack.manufacturer, rack.model, rack.location_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (filterLocation && String(rack.location_id ?? '') !== filterLocation) return false;
      if (filterDesign === 'draft' && rack.status !== 'draft') return false;
      if (filterDesign === 'production' && rack.status === 'draft') return false;
      if (filterStatus === 'active' && !rack.is_active) return false;
      if (filterStatus === 'inactive' && rack.is_active) return false;
      if (filterOccupancy === 'free' && isFull(rack)) return false;
      if (filterOccupancy === 'full' && !isFull(rack)) return false;
      if (filterHeight && String(rack.height_u ?? '') !== filterHeight) return false;
      return true;
    });
  }, [racks, search, filterDesign, filterLocation, filterStatus, filterOccupancy, filterHeight]);

  // Orden de las fichas congelado mientras la ficha está abierta: el del
  // listado tal y como se ve (filtros incluidos).
  const navRacks = useNavOrder(filteredRacks, selectedRack, showForm);

  const exportRows = useMemo(() => filteredRacks.map((rack) => ({
    Nombre: rack.name || '',
    Código: rack.code || '',
    Ubicación: rack.location_name || '',
    'Altura (U)': rack.height_u ?? '',
    'Ancho (mm)': rack.width_mm ?? '',
    'Fondo (mm)': rack.depth_mm ?? '',
    Fabricante: rack.manufacturer || '',
    Modelo: rack.model || '',
    'Nº de serie': rack.serial_number || '',
    Elementos: rack.elements_count ?? '',
    'U ocupadas': rack.occupied_u_front ?? '',
    'U libres': rack.free_units_front ?? '',
    Estado: rack.status === 'draft' ? 'Borrador' : (rack.is_active ? 'Activo' : 'Inactivo'),
    'Grupo de diseño': rack.design_group || '',
  })), [filteredRacks]);

  /* ── Columnas ───────────────────────────────────────────────────────── */
  const measurements = (rack) => {
    const parts = [];
    if (rack.width_mm) parts.push(`${rack.width_mm}`);
    if (rack.depth_mm) parts.push(`${rack.depth_mm}`);
    return parts.length ? `${parts.join(' × ')} mm` : '';
  };

  const columns = useMemo(() => [
    {
      key: 'name',
      label: tr('rack.name', 'Nombre'),
      sortable: true,
      sortValue: (rack) => rack.name,
      render: (rack) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RectangleStackIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
          {rack.name}
        </span>
      ),
    },
    {
      key: 'code',
      label: tr('rack.code', 'Código'),
      sortable: true,
      render: (rack) => (rack.code ? <span className="list-code">{rack.code}</span> : <span className="list-hint">—</span>),
    },
    {
      key: 'location_name',
      label: tr('rack.location', 'Ubicación'),
      sortable: true,
      sortValue: (rack) => rack.location_name || '',
      render: (rack) => rack.location_name || <span className="list-hint">—</span>,
    },
    {
      key: 'height_u',
      label: tr('rack.height', 'Altura (U)'),
      sortable: true,
      sortValue: (rack) => rack.height_u || 0,
      render: (rack) => (rack.height_u ? `${rack.height_u}U` : <span className="list-hint">—</span>),
    },
    {
      key: 'measurements',
      label: tr('rack.measurements', 'Ancho × Fondo'),
      render: (rack) => measurements(rack) || <span className="list-hint">—</span>,
    },
    {
      key: 'occupancy',
      label: tr('rack.occupancy', 'Ocupación'),
      sortable: true,
      sortValue: (rack) => rack.free_units_front ?? -1,
      render: (rack) => {
        if (rack.free_units_front == null) return <span className="list-hint">—</span>;
        const full = rack.free_units_front === 0;
        return (
          <span
            className={`list-badge ${full ? 'list-badge--warn' : 'list-badge--on'}`}
            title={`${rack.occupied_u_front} de ${rack.height_u} U ocupadas · ${rack.elements_count} elementos montados`}
          >
            {full ? tr('rack.full', 'Lleno') : `${rack.free_units_front} U libres`}
          </span>
        );
      },
    },
    {
      key: 'manufacturer',
      label: tr('rack.manufacturer', 'Fabricante'),
      sortable: true,
      render: (rack) => rack.manufacturer || <span className="list-hint">—</span>,
    },
    {
      key: 'is_active',
      label: tr('rack.status', 'Estado'),
      sortable: true,
      sortValue: (rack) => (rack.status === 'draft' ? 2 : (rack.is_active ? 1 : 0)),
      render: (rack) => (
        rack.status === 'draft' ? (
          <span className="list-badge list-badge--muted" title={tr('rack.draft_hint', 'Diseño: no entra en inventario ni consume numeración')}>
            {tr('rack.draft', 'Borrador')}
          </span>
        ) : (
          <span className={`list-badge ${rack.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
            {rack.is_active ? tr('rack.active', 'Activo') : tr('rack.inactive', 'Inactivo')}
          </span>
        )
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, tr]);

  /* ── Filtros ────────────────────────────────────────────────────────── */
  const filters = [
    {
      key: 'design',
      label: tr('rack.filter_design', 'Diseño'),
      allLabel: tr('rack.filter_all_design', 'Producción y diseño'),
      value: filterDesign,
      onChange: setFilterDesign,
      options: [
        { value: 'production', label: tr('rack.filter_production', 'Solo producción') },
        { value: 'draft', label: tr('rack.filter_drafts', 'Solo diseños') },
      ],
    },
    {
      key: 'location',
      label: tr('rack.filter_location', 'Ubicación'),
      allLabel: tr('rack.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLocation,
      onChange: setFilterLocation,
      options: locationOptions,
    },
    {
      key: 'status',
      label: tr('rack.filter_status', 'Estado'),
      allLabel: tr('rack.filter_all_states', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('rack.filter_active', 'Activos') },
        { value: 'inactive', label: tr('rack.filter_inactive', 'Inactivos') },
      ],
    },
    {
      key: 'occupancy',
      label: tr('rack.filter_occupancy', 'Ocupación'),
      allLabel: tr('rack.filter_all_occupancy', 'Cualquier ocupación'),
      value: filterOccupancy,
      onChange: setFilterOccupancy,
      options: [
        { value: 'free', label: tr('rack.filter_with_free', 'Con hueco libre') },
        { value: 'full', label: tr('rack.filter_full', 'Llenos') },
      ],
    },
    {
      key: 'height',
      label: tr('rack.filter_height', 'Altura'),
      allLabel: tr('rack.filter_all_heights', 'Cualquier altura'),
      value: filterHeight,
      onChange: setFilterHeight,
      options: heightOptions,
    },
  ];

  /* ── Vistas ─────────────────────────────────────────────────────────── */
  const RACK_VIEWS = useMemo(() => [
    { key: 'list', label: tr('rack.view_list', 'Lista'), icon: ListBulletIcon, title: tr('rack.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('rack.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('rack.view_cards_hint', 'Tarjetas con lo esencial de cada rack'), render: renderRackCards },
    {
      key: 'front',
      label: tr('rack.view_front', 'Frontal'),
      icon: Square3Stack3DIcon,
      title: tr('rack.view_front_hint', 'Los armarios de frente, con cada equipo en su U y sus cables'),
      render: renderRackFront,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);
  // Solo interesa registrar la preferencia (`listKey`) y que ListPage pinte la
  // vista elegida; el valor concreto lo pide ListPage por su cuenta.
  useListView('racks', 'list');

  const fullCount = racks.filter(isFull).length;
  const draftCount = racks.filter((rack) => rack.status === 'draft').length;
  // Contador visible de inactivos: si un rack se desactiva y no aparece en el
  // subtítulo, hay que «fijarse en todo» para saber que sigue existiendo.
  const inactiveCount = racks.filter((rack) => !rack.is_active).length;
  const subtitle = `${racks.length} ${tr('rack.plural', 'racks')}`
    + (draftCount ? ` · ${draftCount} ${tr('rack.drafts_plural', 'en diseño')}` : '')
    + (inactiveCount ? ` · ${inactiveCount} ${tr('rack.inactive_plural', 'inactivos')}` : '')
    + (fullCount ? ` · ${fullCount} ${tr('rack.full_plural', 'llenos')}` : '')
    + ` · ${filteredRacks.length} ${tr('location.filtered', 'filtrados')}`
    + (location ? ` · ${tr('rack.of_location', 'de')} ${location.name}` : '');

  /** Vista «Frontal»: los armarios filtrados, de frente y con sus cables. */
  function renderRackFront({ rows }) {
    return (
      <RacksFrontView
        companyId={companyId}
        racks={rows}
        onOpenRack={handleOpen}
      />
    );
  }

  /** Tarjetas: lo esencial de cada rack, con los mismos botones de fila. */
  function renderRackCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((rack) => (
          <div
            key={rack.id}
            className="list-card"
            role="button"
            tabIndex={0}
            onClick={() => handleOpen(rack)}
            onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(rack); }}
            title={`${tr('action.view', 'Ver')} ${rack.name}`}
          >
            <div className="list-card-head">
              <div>
                <div className="list-card-title">{rack.name}</div>
                {rack.code && <span className="list-code">{rack.code}</span>}
              </div>
              {rack.status === 'draft' ? (
                <span className="list-badge list-badge--muted">{tr('rack.draft', 'Borrador')}</span>
              ) : (
                <span className={`list-badge ${rack.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
                  {rack.is_active ? tr('rack.active', 'Activo') : tr('rack.inactive', 'Inactivo')}
                </span>
              )}
            </div>

            <div className="list-card-meta">
              {rack.location_name && <span>📍 {rack.location_name}</span>}
              {rack.height_u && <span>{rack.height_u}U</span>}
              {measurements(rack) && <span>{measurements(rack)}</span>}
              {rack.manufacturer && <span>🔧 {rack.manufacturer}</span>}
            </div>

            {rack.free_units_front != null && (
              <div>
                <span className={`list-badge ${isFull(rack) ? 'list-badge--warn' : 'list-badge--on'}`}>
                  {isFull(rack)
                    ? tr('rack.full', 'Lleno')
                    : `${rack.free_units_front} ${tr('rack.units_free', 'U libres')}`}
                </span>
                <span className="list-hint" style={{ marginLeft: 8 }}>
                  {rack.elements_count} {tr('rack.elements', 'elementos')}
                </span>
              </div>
            )}

            <div className="list-card-actions">{rowActions(rack)}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <RackFormElegant
          rack={selectedRack}
          locationId={filterLocation || locationId || null}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha}
          initialStatus={filterDesign === 'draft' && !selectedRack ? 'draft' : 'live'}
          // Orden para pasar de ficha con el contador ‹ n / total ›: el del
          // listado tal y como se ve (filtros y ordenación incluidos), congelado
          // para que guardar no cambie la secuencia.
          siblingRacks={navRacks}
          onNavigate={(destino) => setSelectedRack(destino)}
          onClose={handleFormClose}
          onSave={(creado) => {
            fetchRacks();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setSelectedRack(creado); setFormMode('edit'); }
          }}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={RectangleStackIcon}
          title={tr('rack.title', 'Racks')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('rack.new', 'Nuevo rack')}
          createDisabled={!companyId}
          headerActions={<HelpDot topic="racks.list" label="Ayuda: el listado de racks" />}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('rack.search_placeholder', 'Buscar por nombre, código, fabricante o modelo…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="racks"
          exportSheetName="Racks"

          columns={columns}
          columnStorageKey="cmdb_cols_racks"
          companyId={companyId}
          importEntity="racks"
          onImported={() => fetchRacks()}
          rows={filteredRacks}
          rowKey={(rack) => rack.id}
          onRowClick={handleOpen}
          rowTitle={(rack) => `${tr('action.view', 'Ver')} ${rack.name}`}

          views={RACK_VIEWS}
          listKey="racks"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando racks…')}
          error={error}

          empty={{
            icon: '🗂️',
            title: racks.length === 0
              ? tr('msg.no_data', 'No hay racks todavía')
              : tr('msg.no_results', 'Ningún rack coincide con los filtros'),
            subtitle: racks.length === 0 ? tr('rack.empty_hint', 'Crea el primero para empezar.') : undefined,
            action: racks.length === 0 && companyId ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('rack.new', 'Nuevo rack')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (rack) => (
              <>
                {rack.status === 'draft' && (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => openPromote(rack)}
                    title={tr('rack.promote', 'Pasar a producción')}
                  >
                    <RocketLaunchIcon />
                  </button>
                )}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/infrastructure/rack/${rack.id}`)}
                  title={tr('rack.view_graphic', 'Ver rack (visor gráfico)')}
                >
                  <RectangleStackIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openInspect(rack)}
                  title={tr('rack.view_elements', 'Ver elementos montados')}
                >
                  <Square3Stack3DIcon />
                </button>
              </>
            ),
            onCopy: (rack) => { setDialogError(''); setRackToCopy(rack); },
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('rack.delete_title', 'Eliminar rack'),
            deleteMessage: (rack) => (
              <>
                {tr('rack.delete_confirm', '¿Seguro que quieres eliminar el rack')} <strong>{rack.name}</strong>?{' '}
                {rack.elements_count > 0
                  ? tr('rack.delete_with_elements', `Se eliminarán también sus ${rack.elements_count} elementos montados.`)
                  : tr('rack.delete_hint', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: copiar rack */}
      {rackToCopy && (
        <CloneDialog
          title={tr('rack.copy_title', 'Copiar rack')}
          nameLabel={tr('rack.copy_name', 'Nombre de la copia')}
          placeholder={tr('rack.copy_placeholder', 'Nombre del nuevo rack…')}
          defaultName={`${rackToCopy.name} (copia)`}
          hint={tr('rack.copy_hint', 'Se copiarán las medidas, el fabricante, el modelo, las notas y la ubicación. No se copiarán el código ni el número de serie, y el rack nuevo nace vacío (sin elementos montados ni paneles ciegos).')}
          busy={busy}
          error={dialogError}
          onClose={() => { setRackToCopy(null); setDialogError(''); }}
          onConfirm={handleCopy}
        />
      )}

      {/* Diálogo: pasar un diseño a producción */}
      {rackToPromote && (
        <PromoteRackDialog
          rack={rackToPromote}
          companyId={companyId}
          onClose={() => setRackToPromote(null)}
          onPromoted={() => { setRackToPromote(null); fetchRacks(); }}
        />
      )}

      {/* Diálogo: elementos montados */}
      {rackToInspect && (
        <Dialog
          title={`${tr('rack.elements_title', 'Elementos montados en')} ${rackToInspect.name}`}
          icon="🧩"
          maxWidth={620}
          onClose={() => { setRackToInspect(null); setInspectData(null); setDialogError(''); }}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => navigate(`/infrastructure/rack/${rackToInspect.id}`)}
              >
                {tr('rack.view_graphic', 'Ver rack (visor gráfico)')}
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={() => { setRackToInspect(null); setInspectData(null); }}
              >
                {tr('action.close', 'Cerrar')}
              </button>
            </>
          )}
        >
          {inspectLoading && <p className="list-hint">{tr('msg.loading', 'Cargando…')}</p>}
          {dialogError && <div className="alert alert-danger py-2" role="alert">{dialogError}</div>}
          {inspectData && (
            (inspectData.elements || []).length === 0 ? (
              <p>{tr('rack.no_elements', 'Este rack no tiene elementos montados.')}</p>
            ) : (
              <table className="list-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>{tr('rack.el_position', 'Posición')}</th>
                    <th>{tr('rack.el_name', 'Elemento')}</th>
                    <th>{tr('rack.el_size', 'Tamaño')}</th>
                    <th>{tr('rack.el_side', 'Cara')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(inspectData.elements || []).map((el) => (
                    <tr key={el.id}>
                      <td><span className="list-code">{el.position_u ?? '—'}U</span></td>
                      <td>{el.name || el.code || el.element_type}</td>
                      <td>{el.size_u ? `${el.size_u}U` : '—'}</td>
                      <td>{el.position_front ? tr('rack.side_front', 'Frontal') : tr('rack.side_rear', 'Trasera')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </Dialog>
      )}
    </>
  );
}

export default RacksList;
